import { FEEDBACK_STATUS, FeedbackData, ReleaseData } from "../../types";

// The merged items a tagged release actually carries — the ones Sam's prod
// deploy of that tag puts live. Stricter than the display predicate in
// ReleaseManagerPanel: a tagged release's itemIds is frozen, so an item that
// was re-homed to a later release still sits in the old list but carries the
// new releaseId. Only trust itemIds when the item has no releaseId at all
// (the manager adds dev-fix items that way).
export const releasedCandidates = (release: ReleaseData, feedback: FeedbackData[]): FeedbackData[] =>
  feedback.filter(
    (f) =>
      f.status === FEEDBACK_STATUS.MERGED &&
      (f.releaseId === release.id || (!f.releaseId && (release.itemIds ?? []).includes(f.id))),
  );

export type UpdateResult = { success?: boolean; error?: string; statusCode?: number };
export type UpdateFn = (input: {
  id: string;
  createdAt: string;
  userId: string;
  status: typeof FEEDBACK_STATUS.RELEASED;
  expectedStatus: typeof FEEDBACK_STATUS.MERGED;
}) => Promise<UpdateResult>;

export type MarkReleasedOutcome = {
  released: string[];
  failed: { id: string; error: string }[];
};

// One guarded PATCH per merged item, in order. The batch is bounded by the
// release's item list (capped server-side) and there is no batch endpoint.
// Every item is reported: a stale one (changed since the log was loaded) gets
// the backend's 409 and lands in `failed` instead of being skipped silently,
// and a thrown call (network, non-JSON gateway error) is captured the same way
// so the rest of the batch still runs.
export const markReleased = async (items: FeedbackData[], update: UpdateFn): Promise<MarkReleasedOutcome> => {
  const outcome: MarkReleasedOutcome = { released: [], failed: [] };
  for (const item of items) {
    if (item.status !== FEEDBACK_STATUS.MERGED) continue;
    try {
      const res = await update({
        id: item.id,
        createdAt: item.createdAt,
        userId: item.userId,
        status: FEEDBACK_STATUS.RELEASED,
        expectedStatus: FEEDBACK_STATUS.MERGED,
      });
      if (res?.success) outcome.released.push(item.id);
      else outcome.failed.push({ id: item.id, error: describeFailure(res) });
    } catch (e) {
      outcome.failed.push({ id: item.id, error: e instanceof Error ? e.message : String(e) });
    }
  }
  return outcome;
};

const describeFailure = (res: UpdateResult | null | undefined): string => {
  if (res?.statusCode === 409) return "already changed (409)";
  return res?.error ?? (res?.statusCode ? `HTTP ${res.statusCode}` : "update failed");
};

// Short admin-facing line for the panel, e.g. "Released 3 · 1 not released: abc: already changed (409)".
export const describeOutcome = (outcome: MarkReleasedOutcome): string => {
  const head = `Released ${outcome.released.length}`;
  if (outcome.failed.length === 0) return head;
  return `${head} · ${outcome.failed.length} not released: ${outcome.failed
    .map((f) => `${f.id}: ${f.error}`)
    .join("; ")}`;
};
